import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import {
  StepHeader,
  OnboardingButton,
  OnboardingSkipButton,
  OnboardingInput,
  EditBusinessModal,
  type EditBusinessFormData,
} from '../components'
import { useOnboardingStore } from '@/stores/onboardingStore'
import { useSavePlaceDetails, useLeadPlaceDetails } from '@/hooks/api/useLeadOnboarding'
import {
  MarkerPin03Icon,
  Phone01Icon,
  Globe04Icon,
  Building02Icon,
  Edit05Icon,
  XCloseIcon,
} from '@untitledui-icons/react/line'

// Small single-field modal for quick edits
function QuickEditModal({
  isOpen,
  onClose,
  title,
  label,
  value,
  onSave,
  type = 'text',
}: {
  isOpen: boolean
  onClose: () => void
  title: string
  label: string
  value: string
  onSave: (value: string) => void
  type?: string
}) {
  const [fieldValue, setFieldValue] = useState(value)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) {
      setFieldValue(value)
      const timer = setTimeout(() => inputRef.current?.focus(), 100)
      return () => clearTimeout(timer)
    }
  }, [isOpen, value])

  if (!isOpen) return null

  return createPortal(
    <div className="onboarding-layout fixed inset-0 z-50 flex items-center justify-center p-4" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div className="absolute inset-0 bg-[rgba(10,13,18,0.64)]" style={{ opacity: 0.7 }} onClick={onClose} />
      <div className="relative w-full max-w-[400px] rounded-2xl bg-white shadow-xl">
        <form onSubmit={(e) => { e.preventDefault(); onSave(fieldValue); onClose() }}>
          <div className="relative px-6 pt-6 pb-4">
            <h2 className="text-lg font-semibold text-[#181d27] leading-7 pr-8">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-3 flex h-10 w-10 cursor-pointer items-center justify-center rounded-lg text-[#a4a7ae] hover:bg-[#f5f5f5] hover:text-[#535862]"
            >
              <XCloseIcon className="h-5 w-5" />
            </button>
          </div>
          <div className="px-6 pb-5">
            <OnboardingInput
              ref={inputRef}
              label={label}
              type={type}
              value={fieldValue}
              onChange={(e) => setFieldValue(e.target.value)}
            />
          </div>
          <div className="flex gap-3 px-6 pb-6">
            <button
              type="button"
              onClick={onClose}
              className="flex flex-1 cursor-pointer items-center justify-center rounded-lg border border-[#d5d7da] bg-white px-4 py-2.5 text-base font-semibold text-[#414651] shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] transition-colors hover:bg-[#f9fafb]"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex flex-1 cursor-pointer items-center justify-center rounded-lg border-2 border-[rgba(255,255,255,0.12)] bg-[#222] px-4 py-2.5 text-base font-semibold text-white shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] transition-colors hover:bg-[#333]"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  )
}

interface PlaceConfirmStepProps {
  onNext: () => void
  onBack: () => void
}

export function PlaceConfirmStep({ onNext, onBack }: PlaceConfirmStepProps) {
  const { t } = useTranslation()
  const { placeDetails, setPlaceDetails, leadId, businessType, setBookingDetectionStarted } = useOnboardingStore()
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editFocusField, setEditFocusField] = useState<'addressLine1' | 'restaurantGroup' | undefined>(undefined)
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false)
  const [isWebsiteModalOpen, setIsWebsiteModalOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  
  const savePlaceDetails = useSavePlaceDetails()
  
  // Fetch full place details (phone, website, etc.) from Google Places API
  const { data: fullPlaceDetails, isLoading: isLoadingDetails } = useLeadPlaceDetails(
    placeDetails?.google_place_id || null,
    leadId,
    !!placeDetails?.google_place_id
  )
  
  // Enrich place details with full data when it arrives
  useEffect(() => {
    if (fullPlaceDetails && placeDetails) {
      // The API may return data at top level or nested under 'result'
      const raw = fullPlaceDetails as unknown as Record<string, unknown>
      const data = (raw.result as Record<string, unknown>) || raw

      // Phone may come under various field names from Google Places API
      const phone =
        (data.phone as string) ||
        (data.formatted_phone_number as string) ||
        (data.international_phone_number as string) ||
        (data.internationalPhoneNumber as string) ||
        (data.nationalPhoneNumber as string) ||
        (data.phone_number as string) ||
        ''
      const website = (data.website as string) || ''
      const address =
        (data.address as string) ||
        (data.formatted_address as string) ||
        ''

      // Log raw response for debugging phone issues
      if (!phone) {
        console.warn('[PlaceConfirmStep] No phone found in place details response. Keys:', Object.keys(data))
      }

      if (phone || website || address) {
        setPlaceDetails({
          ...placeDetails,
          phone: phone || placeDetails.phone,
          website: website || placeDetails.website,
          address: address || placeDetails.address,
        })
      }
    }
  }, [fullPlaceDetails]) // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate back if no place details (in useEffect to avoid setState during render)
  useEffect(() => {
    if (!placeDetails) {
      onBack()
    }
  }, [placeDetails, onBack])

  if (!placeDetails) {
    return null
  }

  const handleSaveEdit = (data: EditBusinessFormData) => {
    // Build the combined address for display/API
    const fullAddress = [
      data.addressLine1,
      data.addressLine2,
      `${data.postcode} ${data.city}`.trim(),
    ]
      .filter(Boolean)
      .join(', ')

    // Store both the combined address and individual components
    setPlaceDetails({
      ...placeDetails,
      address: fullAddress,
      phone: data.phone,
      website: data.website,
      // Store individual components for re-editing
      addressLine1: data.addressLine1,
      addressLine2: data.addressLine2,
      postcode: data.postcode,
      city: data.city,
      restaurantGroup: data.restaurantGroup,
      useSamePhoneForEvents: data.useSamePhoneForEvents,
    })
  }

  return (
    <div className="flex w-full flex-1 flex-col gap-6">
      <StepHeader
        title={t('resources.signup.place_confirm.title', { name: placeDetails.name })}
        onBack={onBack}
      />

      <div className="flex flex-1 flex-col gap-8">
        {/* Map preview - Google Maps embed (no API key required) */}
        <div className="h-40 w-full overflow-hidden rounded-2xl border border-[rgba(34,34,34,0.08)] bg-white">
          <iframe
            title="Business location"
            className="h-full w-full border-0 grayscale-[20%]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            src={`https://maps.google.com/maps?q=${encodeURIComponent(placeDetails.address || placeDetails.name)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
          />
        </div>

        {/* Business information section */}
        <div className="flex flex-col gap-3">
          {/* Header row */}
          <div className="flex items-center justify-between">
            <p className="text-base font-semibold leading-6 text-[#222]">
              {t('resources.signup.place_confirm.business_info')}
              {isLoadingDetails && (
                <span className="ml-2 text-xs font-normal text-[#717680]">
                  {t('resources.signup.place_confirm.loading_details')}
                </span>
              )}
            </p>
            <button
              type="button"
              onClick={() => { setEditFocusField('addressLine1'); setIsEditModalOpen(true) }}
              className="flex cursor-pointer items-center gap-1 text-sm font-semibold text-[#368f8b] transition-colors hover:text-[#2a7370]"
            >
              {t('resources.signup.place_confirm.edit')}
              <Edit05Icon className="h-5 w-5" />
            </button>
          </div>

          {/* Info rows */}
          <div className="flex flex-col gap-4">
            {/* Address */}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <MarkerPin03Icon className="h-6 w-6 text-[#333]" />
              </div>
              <p className="flex-1 text-base font-normal leading-6 text-[#181d27]">
                {placeDetails.address}
              </p>
            </div>

            {/* Phone */}
            {placeDetails.phone && (
              <div className="flex items-center gap-4">
                <div className="flex-shrink-0">
                  <Phone01Icon className="h-6 w-6 text-[#333]" />
                </div>
                <p className="flex-1 text-base font-normal leading-6 text-[#181d27]">
                  {placeDetails.phone}
                </p>
              </div>
            )}

            {/* Website */}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <Globe04Icon className="h-6 w-6 text-[#333]" />
              </div>
              {placeDetails.website ? (
                <a
                  href={placeDetails.website.startsWith('http') ? placeDetails.website : `https://${placeDetails.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 cursor-pointer text-base font-normal leading-6 text-[#222] hover:underline"
                >
                  {placeDetails.website}
                </a>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsWebsiteModalOpen(true)}
                  className="flex-1 cursor-pointer text-left text-base font-medium leading-6 text-[#368f8b] hover:underline"
                >
                  {t('resources.signup.place_confirm.add_website', 'Add link to website (optional)')}
                </button>
              )}
            </div>

            {/* Restaurant group */}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <Building02Icon className="h-6 w-6 text-[#333]" />
              </div>
              {placeDetails.restaurantGroup ? (
                <p className="flex-1 text-base font-normal leading-6 text-[#181d27]">
                  {placeDetails.restaurantGroup}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsGroupModalOpen(true)}
                  className="flex-1 cursor-pointer text-left text-base font-medium leading-6 text-[#368f8b] hover:underline"
                >
                  {businessType === 'HOTEL'
                  ? t('resources.signup.place_confirm.hotel_group')
                  : businessType === 'SMALL_BUSINESS'
                    ? t('resources.signup.place_confirm.smb_group')
                    : t('resources.signup.place_confirm.restaurant_group')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-auto flex flex-col gap-2 pt-4 lg:mt-0 lg:pt-0">
          <OnboardingButton
            onClick={async () => {
              if (!leadId || !placeDetails) return

              setIsSaving(true)
              try {
                // Save place details to backend - this triggers async booking detection
                await savePlaceDetails.mutateAsync({
                  leadId,
                  data: {
                    business_name: placeDetails.name,
                    business_address: placeDetails.address,
                    business_phone: placeDetails.phone,
                    website: placeDetails.website,
                    google_place_id: placeDetails.google_place_id,
                    use_same_phone_for_events: placeDetails.useSamePhoneForEvents ?? false,
                  },
                })

                // Start polling for booking detection for restaurants
                if (businessType === 'RESTAURANT') {
                  setBookingDetectionStarted(true)
                }

                // Continue to next step
                onNext()
              } catch (error) {
                console.error('Failed to save place data:', error)
                // Still proceed even if save fails
                onNext()
              } finally {
                setIsSaving(false)
              }
            }}
            isLoading={isSaving}
          >
            {t('resources.signup.common.yes_continue')}
          </OnboardingButton>
          <OnboardingSkipButton onClick={onBack}>{t('resources.signup.common.search_again')}</OnboardingSkipButton>
        </div>
      </div>

      {/* Full edit modal */}
      <EditBusinessModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveEdit}
        placeDetails={placeDetails}
        focusField={editFocusField}
        businessType={businessType}
      />

      {/* Quick edit: Group name */}
      <QuickEditModal
        isOpen={isGroupModalOpen}
        onClose={() => setIsGroupModalOpen(false)}
        title={
          businessType === 'HOTEL'
            ? t('resources.signup.place_confirm.add_group_title_hotel')
            : businessType === 'SMALL_BUSINESS'
              ? t('resources.signup.place_confirm.add_group_title_smb')
              : t('resources.signup.place_confirm.add_group_title', 'Add restaurant group name')
        }
        label={
          businessType === 'HOTEL'
            ? t('resources.signup.place_confirm.hotel_group')
            : businessType === 'SMALL_BUSINESS'
              ? t('resources.signup.place_confirm.smb_group')
              : t('resources.signup.place_confirm.restaurant_group')
        }
        value={placeDetails.restaurantGroup || ''}
        onSave={(val) => setPlaceDetails({ ...placeDetails, restaurantGroup: val })}
      />

      {/* Quick edit: Website */}
      <QuickEditModal
        isOpen={isWebsiteModalOpen}
        onClose={() => setIsWebsiteModalOpen(false)}
        title={t('resources.signup.place_confirm.add_website_title', 'Add website')}
        label={t('resources.signup.place_confirm.website_label', 'Website')}
        value={placeDetails.website || ''}
        onSave={(val) => setPlaceDetails({ ...placeDetails, website: val })}
        type="url"
      />
    </div>
  )
}
